import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { fetchAuthMethods, requestPasswordReset } from '@/auth/authClient';
import { isInBitrix } from '@/providers/bitrixClient';
import { LogIn, Loader2, Eye, EyeOff } from 'lucide-react';

/**
 * Экран входа. Внутри портала Битрикса — кнопка «Войти через Битрикс» (управленцы
 * без пароля). Standalone — форма логин/пароль для линейного персонала, плюс та же
 * кнопка «Войти через Битрикс24»: она уводит на портал и возвращает уже с сессией,
 * чтобы управленцы могли открывать ERP по прямой ссылке (например, с телефона).
 */
export function LoginScreen() {
  const login = useStore((s) => s.login);
  const authError = useStore((s) => s.authError);
  const toast = useStore((s) => s.toast);

  const inBitrix = isInBitrix();
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submitBitrix = async () => {
    // Внутри портала самый надёжный повторный вход — перезагрузка страницы: SDK Битрикса
    // заново получает СВЕЖИЙ токен и вход проходит автоматически. Иначе после выхода
    // повторная авторизация шла устаревшим токеном и падала (приходилось переоткрывать).
    setBusy(true);
    window.location.reload();
  };

  // Снаружи портала кнопку Битрикса показываем, только если вход настроен (есть ключи
  // приложения). Иначе человек упрётся в страницу «не настроено» — лучше не дразнить.
  // Адрес портала берём с сервера: уводить по нему должен браузер, иначе шлюз VibeCode
  // сам пройдёт по перенаправлению и отдаст страницу портала под нашим доменом.
  const [authorizeUrl, setAuthorizeUrl] = useState<string | null>(null);
  useEffect(() => {
    if (inBitrix) return;
    let alive = true;
    fetchAuthMethods().then((m) => {
      if (alive && m.bitrix_oauth && m.authorize_url) setAuthorizeUrl(m.authorize_url);
    });
    return () => { alive = false; };
  }, [inBitrix]);

  const startBitrixOAuth = () => {
    if (!authorizeUrl) return;
    setBusy(true);
    window.location.href = authorizeUrl;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginValue.trim() || !password || busy) return;
    setBusy(true);
    try {
      await login(loginValue.trim(), password);
    } catch {
      /* ошибка показана через authError */
    } finally {
      setBusy(false);
    }
  };

  const [forgotShown, setForgotShown] = useState(false);
  const forgot = async () => {
    // Сброс пароля — через администратора (без почты): супер-админ задаёт новый пароль
    // в «Доступы» → пользователь → «Сменить пароль». Регистрируем заявку, если логин введён.
    if (loginValue.trim()) await requestPasswordReset(loginValue.trim()).catch(() => undefined);
    setForgotShown(true); // постоянное сообщение на экране (тост мелькает — его не видно)
    toast({ kind: 'info', text: 'Пароль меняет администратор — обратитесь к нему.' });
  };

  return (
    // Прокрутка + центрирование через my-auto — чтобы на невысоком экране (телефон с открытой
    // клавиатурой) низ формы с кнопкой не обрезался, а докручивался.
    <div className="h-full overflow-y-auto flex justify-center bg-surface-0 text-ink-1 p-4">
      <form onSubmit={submit} className="w-full max-w-sm my-auto bg-surface-1 border border-line rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl font-black bg-gradient-to-br from-accent to-warm-coral bg-clip-text text-transparent">
            +ERP
          </span>
        </div>
        <h1 className="text-lg font-semibold mb-5">Вход</h1>

        {inBitrix ? (
          <>
            <button type="button" onClick={submitBitrix} disabled={busy}
              className="btn-primary w-full justify-center">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Войти через Битрикс24
            </button>
            {authError && <p className="mt-3 text-sm text-status-refused">{authError}</p>}
            <p className="text-[11px] text-ink-3 mt-5 text-center leading-relaxed">
              Вы внутри портала Битрикс24 — пароль не нужен, вход по вашей учётной записи портала.
            </p>
          </>
        ) : (
          <>
            {authorizeUrl && (
              <>
                <button type="button" onClick={startBitrixOAuth} disabled={busy}
                  className="btn-primary w-full justify-center">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                  Войти через Битрикс24
                </button>
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px flex-1 bg-line" />
                  <span className="text-[11px] text-ink-3">или по логину</span>
                  <div className="h-px flex-1 bg-line" />
                </div>
              </>
            )}

            <div className="space-y-3">
              <div>
                <label className="field-label">Логин</label>
                <input
                  className="input" autoFocus autoComplete="username" value={loginValue}
                  onChange={(e) => setLoginValue(e.target.value)} disabled={busy}
                />
              </div>
              <div>
                <label className="field-label">Пароль</label>
                <div className="relative">
                  <input
                    className="input pr-10" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} disabled={busy}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={busy}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-1 p-1"
                    title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {authError && <p className="mt-3 text-sm text-status-refused">{authError}</p>}

            <button type="submit" className="btn-primary w-full mt-5 justify-center" disabled={busy || !loginValue.trim() || !password}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Войти
            </button>

            <button type="button" className="text-xs text-ink-3 hover:text-ink-2 mt-3 mx-auto block" onClick={forgot}>
              Забыли пароль?
            </button>

            {forgotShown && (
              <div className="mt-3 rounded-lg border border-line bg-surface-2/60 px-3 py-2 text-[12px] text-ink-2 leading-relaxed">
                Пароль меняет <b>администратор</b>: он задаёт вам новый в разделе
                «Доступы и пользователи» → ваша строка → «Сменить пароль».
              </div>
            )}

            <p className="text-[11px] text-ink-3 mt-5 text-center leading-relaxed">
              {authorizeUrl
                ? 'Управленцы входят через Битрикс24 — пароль не нужен.'
                : 'Управленцы заходят из портала Битрикс24 (левое меню / карточка сделки).'}
            </p>
          </>
        )}
      </form>
    </div>
  );
}
